import React, { useState, useEffect } from 'react';
import { ChatBubbleLeftRightIcon, SparklesIcon, DocumentTextIcon, CpuChipIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../utils/api';
import { renderLlmMarkdown } from '../utils/renderLlmGraphInsight';
import { fabricKindLabel } from '../utils/weaveDomain';

interface KnowledgeFabric {
  id: string;
  name: string;
  status: string;
  document_count: number;
  total_chunks?: number;
  created_at: string;
  model_status: string;
  weave_domain?: string;
  fabric_kind_label?: string;
}

function shortRecordId(value?: string): string {
  const id = String(value || '').trim();
  if (!id) return '';
  if (id.length <= 18) return id;
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

const TestLLM: React.FC = () => {
  const [selectedFabric, setSelectedFabric] = useState<string>('');
  const [selectedLLM, setSelectedLLM] = useState<string>('openai');
  const [llmProviders, setLlmProviders] = useState<Array<{ id: string; name: string; description?: string }>>([]);
  const [testQuery, setTestQuery] = useState<string>('');
  const [testResults, setTestResults] = useState<any[]>([]);
  const [sessionByFabric, setSessionByFabric] = useState<Record<string, string>>({});
  const [feedbackDrafts, setFeedbackDrafts] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [fabrics, setFabrics] = useState<KnowledgeFabric[]>([]);
  const [loadingFabrics, setLoadingFabrics] = useState(true);
  const [statusBanner, setStatusBanner] = useState<{ type: 'success' | 'error' | 'info'; message: string } | null>(null);

  // Fetch available fabrics on component mount
  useEffect(() => {
    const fetchFabrics = async () => {
      try {
        const response = await apiRequest('api/v1/knowledge/');
        const data = await response.json();
        
        if (data.success && data.data) {
          setFabrics(data.data);
          if (selectedFabric && !data.data.some((fabric: KnowledgeFabric) => fabric.id === selectedFabric)) {
            setSelectedFabric('');
            setStatusBanner({
              type: 'error',
              message: 'Previously selected fabric is no longer available. Please select another fabric.',
            });
          }
        } else {
          console.error('Failed to fetch fabrics:', data.message);
          setStatusBanner({
            type: 'error',
            message: data.message || 'Unable to load knowledge fabrics.',
          });
        }
      } catch (error) {
        console.error('Error fetching fabrics:', error);
        setStatusBanner({
          type: 'error',
          message: 'Unable to load knowledge fabrics. Please check backend connectivity.',
        });
      } finally {
        setLoadingFabrics(false);
      }
    };

    fetchFabrics();

    const fetchProviders = async () => {
      try {
        const response = await apiRequest('api/v1/knowledge/api-keys/providers');
        const data = await response.json();
        const providers = data?.data?.providers || data?.providers || [];
        if (Array.isArray(providers) && providers.length > 0) {
          setLlmProviders(providers);
          setSelectedLLM(data?.data?.default_provider || providers[0].id);
        }
      } catch (error) {
        console.error('Error fetching LLM providers:', error);
      }
    };
    fetchProviders();
  }, []);

  const handleTestQuery = async () => {
    if (!selectedFabric) {
      setStatusBanner({ type: 'info', message: 'Select a knowledge fabric before running a test.' });
      return;
    }
    if (!testQuery.trim() || !selectedLLM) {
      setStatusBanner({ type: 'info', message: 'Enter a query to run a test.' });
      return;
    }

    setIsLoading(true);
    setStatusBanner(null);
    
    try {
      const response = await apiRequest(`api/v1/knowledge/query/${selectedFabric}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: testQuery,
          llm_provider: selectedLLM,
          retrieve_all: true,
          top_k: 0,
          session_id: sessionByFabric[selectedFabric] || undefined,
        })
      });

      const data = await response.json();
      
      const result = {
        fabricId: selectedFabric,
        llmProvider: selectedLLM,
        query: testQuery,
        response: data.success ? data.data.answer : (data.error || data.message || 'Sorry, I encountered an error. Please try again.'),
        confidence: data.success ? data.data.confidence || 0.85 : 0.0,
        timestamp: new Date().toISOString(),
        relevantChunks: data.success ? (data.data.relevant_chunks ?? data.data.relevant_chunks_found ?? 0) : 0,
        packedChunks: data.success ? (data.data.retrieval?.packed_chunks ?? data.data.relevant_chunks_found ?? 0) : 0,
        processingTime: data.success ? data.data.processing_time || '1.2s' : '0s',
        sessionId: data.success ? data.data.session_id : undefined,
        episodeId: data.success ? data.data.episode_id : undefined,
        feedbackState: null as string | null,
      };

      if (data.success && data.data.session_id) {
        setSessionByFabric((prev) => ({ ...prev, [selectedFabric]: data.data.session_id }));
      }

      setTestResults(prev => [result, ...prev]);
      setStatusBanner({
        type: data.success ? 'success' : 'error',
        message: data.success ? 'Query completed successfully.' : (data.error || data.message || 'Query failed.'),
      });
    } catch (error) {
      const errorResult = {
        fabricId: selectedFabric,
        llmProvider: selectedLLM,
        query: testQuery,
        response: 'Sorry, I encountered an error. Please try again.',
        confidence: 0.0,
        timestamp: new Date().toISOString(),
        relevantChunks: 0,
        processingTime: '0s'
      };
      setTestResults(prev => [errorResult, ...prev]);
      setStatusBanner({
        type: 'error',
        message: 'Query failed. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitFeedback = async (index: number, rating: 'up' | 'down', applyCorrection: boolean) => {
    const result = testResults[index];
    if (!result?.episodeId || !result?.fabricId) {
      setStatusBanner({ type: 'info', message: 'This answer has no episode id yet. Run the query again after restart.' });
      return;
    }
    const correction = (feedbackDrafts[index] || '').trim();
    try {
      const response = await apiRequest('api/v1/agent/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fabric_id: result.fabricId,
          episode_id: result.episodeId,
          session_id: result.sessionId,
          rating,
          outcome: rating === 'up' ? 'helpful' : 'incorrect',
          correction_text: correction || undefined,
        }),
      });
      const data = await response.json();
      if (!data.success) {
        setStatusBanner({ type: 'error', message: data.error || data.message || 'Feedback failed.' });
        return;
      }
      let note = rating === 'up' ? 'Marked helpful.' : 'Correction queued.';
      const candidateId = data.data?.enrichment_candidate_id;
      if (applyCorrection && candidateId) {
        const approve = await apiRequest(`api/v1/agent/enrichment/${candidateId}/approve`, { method: 'POST' });
        const approved = await approve.json();
        note = approved.success
          ? 'Correction written into the fabric. Later answers will use it.'
          : (approved.error || approved.message || 'Could not apply correction.');
      }
      setTestResults((prev) => prev.map((item, i) => (i === index ? { ...item, feedbackState: note } : item)));
      setStatusBanner({ type: 'success', message: note });
    } catch {
      setStatusBanner({ type: 'error', message: 'Feedback request failed.' });
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-[#cbd5e1] [&_.bg-white]:bg-[#10141d]/75 [&_.bg-gray-50]:bg-white/[0.03] [&_.bg-gray-100]:bg-white/[0.05] [&_.text-gray-900]:text-[#e8edf4] [&_.text-gray-800]:text-[#cbd5e1] [&_.text-gray-700]:text-[#cbd5e1] [&_.text-gray-600]:text-[#8b9cb0] [&_.text-gray-500]:text-[#8b9cb0] [&_.text-gray-400]:text-[#8b9cb0] [&_.text-gray-300]:text-[#8b9cb0] [&_.border-gray-200]:border-[rgba(148,163,184,0.11)] [&_.border-gray-300]:border-[rgba(148,163,184,0.2)] [&_input]:bg-[#10141d]/70 [&_input]:text-[#e8edf4] [&_input]:border-[rgba(148,163,184,0.2)] [&_input]:placeholder:text-[#8b9cb0] [&_textarea]:bg-[#10141d]/70 [&_textarea]:text-[#e8edf4] [&_textarea]:border-[rgba(148,163,184,0.2)] [&_textarea]:placeholder:text-[#8b9cb0] [&_select]:bg-[#10141d]/70 [&_select]:text-[#e8edf4] [&_select]:border-[rgba(148,163,184,0.2)]">
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center">
            <ChatBubbleLeftRightIcon className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Test with LLM</h1>
            <p className="text-gray-600">Ask a fabric. CKF keeps the session, the episode, and approved corrections.</p>
          </div>
        </div>
      </div>
      {statusBanner && (
        <div
          className={`mb-6 rounded-xl border px-4 py-3 text-sm ${
            statusBanner.type === 'success'
              ? 'border-emerald-400/35 bg-emerald-500/10 text-emerald-200'
              : statusBanner.type === 'error'
                ? 'border-rose-400/35 bg-rose-500/10 text-rose-200'
                : 'border-cyan-400/35 bg-cyan-500/10 text-cyan-100'
          }`}
        >
          {statusBanner.message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Test Configuration */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <SparklesIcon className="w-5 h-5 mr-2 text-emerald-500" />
            Test Configuration
          </h2>
          
                      <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Knowledge Fabric
                </label>
                <select
                  value={selectedFabric}
                  onChange={(e) => setSelectedFabric(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  disabled={loadingFabrics}
                >
                  <option value="">
                    {loadingFabrics ? 'Loading fabrics...' : 'Choose a fabric...'}
                  </option>
                  {fabrics.map((fabric) => (
                    <option key={fabric.id} value={fabric.id}>
                      {fabric.name} · {fabricKindLabel(fabric.weave_domain)} ({fabric.document_count} docs)
                    </option>
                  ))}
                </select>
                {selectedFabric && (
                  <div className="mt-3 rounded-xl border border-[rgba(148,163,184,0.14)] bg-white/[0.03] px-3 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.16em] text-[#8b9cb0]">Selected fabric</p>
                        <p className="mt-1 text-sm font-medium leading-snug text-[#e8edf4]">
                          {fabrics.find(f => f.id === selectedFabric)?.name}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full border border-[rgba(155,139,212,0.4)] bg-gradient-to-r from-[rgba(94,200,242,0.12)] to-[rgba(155,139,212,0.18)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#d7cff5]">
                        CKF ready
                      </span>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-[#8b9cb0]">
                      <span className="text-[#cbd5e1]">
                        {fabricKindLabel(fabrics.find(f => f.id === selectedFabric)?.weave_domain)}
                      </span>
                      {' · '}domain-aware reasoning is applied automatically for complex questions.
                    </p>
                    <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div className="rounded-lg border border-[rgba(148,163,184,0.11)] bg-[#10141d]/55 px-2.5 py-2">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-[#8b9cb0]">CKF session</p>
                        <p
                          className="mt-1 truncate font-mono text-[11px] text-[#5ec8f2]"
                          title={sessionByFabric[selectedFabric] || 'Starts on the first query'}
                        >
                          {sessionByFabric[selectedFabric]
                            ? shortRecordId(sessionByFabric[selectedFabric])
                            : 'Starts on first query'}
                        </p>
                      </div>
                      <div className="rounded-lg border border-[rgba(148,163,184,0.11)] bg-[#10141d]/55 px-2.5 py-2">
                        <p className="text-[10px] uppercase tracking-[0.14em] text-[#8b9cb0]">CKF closed loop</p>
                        <p className="mt-1 text-[11px] text-[#cbd5e1]">Approved corrections rewrite this fabric</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select LLM Provider
                </label>
                <select
                  value={selectedLLM}
                  onChange={(e) => setSelectedLLM(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  {(llmProviders.length > 0 ? llmProviders : [{ id: 'openai', name: 'OpenAI GPT-4' }]).map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {llmProviders.find((p) => p.id === selectedLLM)?.description
                    || (selectedLLM === 'bedrock'
                      ? 'Using AWS Bedrock via IAM (no OpenAI key required on AWS)'
                      : 'Using OpenAI for advanced reasoning')}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Test Query
                </label>
                <textarea
                  value={testQuery}
                  onChange={(e) => setTestQuery(e.target.value)}
                  placeholder="Enter your test query here..."
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                />
              </div>

            <button
              onClick={handleTestQuery}
              disabled={!selectedFabric || !testQuery.trim() || isLoading}
              className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-6 py-3 rounded-lg font-medium hover:from-emerald-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Testing...
                </>
              ) : (
                <>
                  <CpuChipIcon className="w-5 h-5 mr-2" />
                  Run Test
                </>
              )}
            </button>
          </div>
        </div>

        {/* Test Results */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
            <DocumentTextIcon className="w-5 h-5 mr-2 text-emerald-500" />
            Test Results
          </h2>
          
          <div className="space-y-4">
            {testResults.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <ChatBubbleLeftRightIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No test results yet. Run a test to see results here.</p>
              </div>
            ) : (
              testResults.map((result, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4">
                  <div className="mb-3 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-[#e8edf4]">
                          {fabrics.find(f => f.id === result.fabricId)?.name || 'Unknown Fabric'}
                        </span>
                        <span className="rounded-full border border-[rgba(148,163,184,0.16)] bg-white/[0.04] px-2 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-[#cbd5e1]">
                          {result.llmProvider || 'LLM'}
                        </span>
                      </div>
                      {(result.sessionId || result.episodeId) && (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {result.sessionId && (
                            <span
                              className="inline-flex items-center gap-2 rounded-md border border-[rgba(148,163,184,0.14)] bg-[#10141d]/55 px-2 py-1"
                              title={result.sessionId}
                            >
                              <span className="text-[10px] uppercase tracking-[0.14em] text-[#8b9cb0]">Session</span>
                              <span className="font-mono text-[11px] text-[#5ec8f2]">{shortRecordId(result.sessionId)}</span>
                            </span>
                          )}
                          {result.episodeId && (
                            <span
                              className="inline-flex items-center gap-2 rounded-md border border-[rgba(155,139,212,0.28)] bg-[rgba(155,139,212,0.08)] px-2 py-1"
                              title={result.episodeId}
                            >
                              <span className="text-[10px] uppercase tracking-[0.14em] text-[#8b9cb0]">Episode</span>
                              <span className="font-mono text-[11px] text-[#d7cff5]">{shortRecordId(result.episodeId)}</span>
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    <span className="shrink-0 text-xs text-[#8b9cb0]">
                      {new Date(result.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  
                  <div className="mb-3">
                    <p className="text-sm text-gray-600 mb-2">
                      <strong>Query:</strong> {result.query}
                    </p>
                    <div className="rounded-lg border border-gray-200 bg-white px-3 py-3">
                      {renderLlmMarkdown(String(result.response || ''), 'light')}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Confidence: {(result.confidence * 100).toFixed(1)}%</span>
                    <span>Processing: {result.processingTime}</span>
                    <span>Chunks: {result.relevantChunks}{result.packedChunks && result.packedChunks !== result.relevantChunks ? ` (packed ${result.packedChunks})` : ''}</span>
                  </div>
                  {result.episodeId && (
                    <div className="mt-3 space-y-2 border-t border-[rgba(148,163,184,0.11)] pt-3">
                      <p className="text-[10px] uppercase tracking-[0.16em] text-[#8b9cb0]">CKF closed loop</p>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => submitFeedback(index, 'up', false)}
                          className="rounded-md border border-[rgba(62,207,155,0.35)] bg-[rgba(62,207,155,0.08)] px-2.5 py-1 text-xs text-[#9af0ca]"
                        >
                          Helpful
                        </button>
                        <button
                          type="button"
                          onClick={() => submitFeedback(index, 'down', false)}
                          className="rounded-md border border-[rgba(240,137,132,0.35)] bg-[rgba(240,137,132,0.08)] px-2.5 py-1 text-xs text-[#f3b4b0]"
                        >
                          Incorrect
                        </button>
                      </div>
                      <textarea
                        value={feedbackDrafts[index] || ''}
                        onChange={(e) => setFeedbackDrafts((prev) => ({ ...prev, [index]: e.target.value }))}
                        placeholder="Correction to write back into this fabric"
                        className="w-full rounded-md border px-2 py-1 text-xs"
                        rows={2}
                      />
                      <button
                        type="button"
                        onClick={() => submitFeedback(index, 'down', true)}
                        className="rounded-md bg-emerald-600 px-2 py-1 text-xs text-white"
                      >
                        Apply correction to fabric
                      </button>
                      {result.feedbackState && (
                        <p className="text-xs text-emerald-200">{result.feedbackState}</p>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Quick Test Examples */}
      <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <SparklesIcon className="w-5 h-5 mr-2 text-emerald-500" />
          Quick Test Examples
        </h3>
        
        {fabrics.length === 0 ? (
          <div className="text-center text-gray-500 py-4">
            <DocumentTextIcon className="w-8 h-8 mx-auto mb-2 text-gray-300" />
            <p>No knowledge fabrics available. Create a fabric first to see test examples.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Fabric-specific examples */}
            {fabrics.map((fabric) => (
              <div key={fabric.id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">{fabric.name}</h4>
                  <span className="text-xs text-gray-500">
                    {fabric.document_count} docs, {fabric.total_chunks || 0} chunks
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[
                    "What are the key stakeholders mentioned in this document?",
                    "Explain the main procedures described in this document",
                    "What are the important requirements or policies?",
                    "Summarize the main topics covered in this document",
                    "What are the key processes or workflows?",
                    "What are the main guidelines or best practices?"
                  ].map((example, index) => (
                    <button
                      key={index}
                      onClick={() => {
                        setSelectedFabric(fabric.id);
                        setTestQuery(example);
                      }}
                      className="text-left p-3 border border-gray-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-colors group"
                    >
                      <p className="text-sm text-gray-700 group-hover:text-emerald-700">{example}</p>
                      <p className="text-xs text-gray-500 mt-1">Click to select fabric & query</p>
                    </button>
                  ))}
                </div>
              </div>
            ))}
            
            {/* General examples */}
            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">General Knowledge Queries</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  "What is the main purpose of this document?",
                  "What are the key findings or conclusions?",
                  "What are the important dates or timelines mentioned?",
                  "What are the main challenges or issues discussed?",
                  "What are the recommendations or next steps?",
                  "What are the key metrics or data points?"
                ].map((example, index) => (
                  <button
                    key={index}
                    onClick={() => setTestQuery(example)}
                    className="text-left p-3 border border-gray-200 rounded-lg hover:border-emerald-300 hover:bg-emerald-50 transition-colors group"
                  >
                    <p className="text-sm text-gray-700 group-hover:text-emerald-700">{example}</p>
                    <p className="text-xs text-gray-500 mt-1">Click to set query only</p>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestLLM; 